import React from 'react';
import { Link } from 'react-scroll';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import {
  BUTTON_THEMES,
  CARD_TYPES,
  SCROLL_WRAPPER_TYPES,
} from '../../../shared/constants';
import { ICard, IContentfulLink } from '../../../types/shared';

import ChainIcon from '../../atoms/icons/ChainIcon';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { getCtaLink } from '../../../utils/generic';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import { languageHelper } from '../../../utils/language';
import { parseSectionID } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import buttonStyles, { CtaWrapper } from '../../../styles/buttonStyles';
import Colors from '../../../styles/colors';
import {
  AbsoluteTop,
  H1,
  HeroSubtitle,
  IntroText,
} from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';
import { bisColorsTheme } from '../../../styles/themes/colors';

import Breadcrumbs from '../../molecules/breadcrumbs/Breadcrumbs';
import { BreadcrumbsProps } from '../../molecules/breadcrumbs/Breadcrumbs.types';
import ContentWrapper from '../../molecules/content-wrapper/ContentWrapper';
import HeaderCtaCard from '../../molecules/header-cta-card/HeaderCtaCard';
import HorizontalScrollWrapper from '../../molecules/horizontal-scroll-wrapper/HorizontalScrollWrapper';

type labelObject = {
  [key: string]: { [key: string]: string };
};

const LABELS: labelObject = {
  navigate: {
    en: 'Navigate directly',
    nl: 'Snel naar',
  },
};

const renderButtons = (
  primary?: IContentfulLink,
  secondary?: IContentfulLink,
  primaryTheme?: string,
  secondaryTheme?: string
) => (
  <StyledCtaWrapper>
    {primary && (
      <StyledPrimaryButton
        type={primary?.type}
        label={primary.label || primary.name}
        buttonTheme={primaryTheme || primary.theme || BUTTON_THEMES.highlight}
        to={getCtaLink(primary)}
        iconPosition={primary?.iconPosition}
        icon={primary?.icon}
        deqComponentTitle={`GenericHeader ${primary.label}`.toLowerCase()}
        deqCustomAttributes={{
          componentType: 'GenericHeader',
          type: 'button',
          elementStyle: 'primary',
          elementTarget: `${getCtaLink(primary)}`,
        }}
      />
    )}
    {secondary && (
      <StyledSecondaryButton
        type={secondary?.type}
        label={secondary.label || secondary.name}
        buttonTheme={secondaryTheme || secondary.theme || BUTTON_THEMES.default}
        to={getCtaLink(secondary)}
        iconPosition={secondary?.iconPosition}
        icon={secondary?.icon}
        deqComponentTitle={`GenericHeader ${secondary.label}`.toLowerCase()}
        deqCustomAttributes={{
          componentType: 'GenericHeader',
          type: 'button',
          elementStyle: 'secondary',
          elementTarget: `${getCtaLink(secondary)}`,
        }}
      />
    )}
  </StyledCtaWrapper>
);

export interface AnchorLink {
  id: string;
  label: string;
}

interface Props {
  header: ICard;
  headerCard?: ICard;
  extraPrimaryLink?: IContentfulLink;
  extraSecondaryLink?: IContentfulLink;
  productName: string;
  anchorLinks?: AnchorLink[];
  showAnchorLinks: boolean;
  breadcrumbsProps?: BreadcrumbsProps;
}

const GenericHeader = ({
  header,
  headerCard,
  extraPrimaryLink,
  extraSecondaryLink,
  productName,
  anchorLinks,
  showAnchorLinks,
  breadcrumbsProps,
}: Props) => {
  const { lang } = languageHelper();
  const hasValidHeader =
    header.title ||
    header.subtitle ||
    header.primaryLink ||
    header.secondaryLink;

  return (
    <Container id="product-header" removeBackground={!header.body}>
      <ImageContainer>
        {header.image && (
          <StyledGatsbyImage
            alt=""
            image={header.image.gatsbyImageData}
            objectPosition="50% 50%"
            alignToContent={!hasValidHeader}
          />
        )}
        <ExtendedContentWrapper>
          {breadcrumbsProps && (
            <StyledBreadcrumbsWrapper>
              <Breadcrumbs {...breadcrumbsProps} />
            </StyledBreadcrumbsWrapper>
          )}
          <TitlesWrapper data-testid="title-wrapper">
            {header.title && <Title>{header.title}</Title>}
            {header.subtitle && (
              <StyledHeroSubtitle>{header.subtitle}</StyledHeroSubtitle>
            )}
            {!header.subtitle && header.body && (
              <IntroText>
                <ReactMarkdownExtended componentType={CARD_TYPES.header}>
                  {header.body.childMarkdownRemark.rawMarkdownBody}
                </ReactMarkdownExtended>
              </IntroText>
            )}
            {(header.primaryLink || header.secondaryLink) &&
              renderButtons(
                header.primaryLink,
                header.secondaryLink,
                BUTTON_THEMES.highlight,
                BUTTON_THEMES.highlight
              )}
          </TitlesWrapper>
        </ExtendedContentWrapper>
        {!headerCard && hasValidHeader && <MobileHeaderTile {...header} />}
      </ImageContainer>
      {header.subtitle && header.body ? (
        <StyledContentWrapper>
          <TextContainer data-testid="body" extraCard={!!headerCard}>
            <StyledIntroText>
              <ReactMarkdownExtended>
                {header.body.childMarkdownRemark.rawMarkdownBody}
              </ReactMarkdownExtended>
              {(extraPrimaryLink || extraSecondaryLink) &&
                !headerCard &&
                renderButtons(
                  extraPrimaryLink,
                  extraSecondaryLink,
                  BUTTON_THEMES.default,
                  BUTTON_THEMES.default
                )}
            </StyledIntroText>
          </TextContainer>
          {headerCard ? (
            <CtaCardContainer>
              <HeaderCtaCard
                ctaLinkPrimary={headerCard.primaryLink}
                ctaLinkSecondary={headerCard.secondaryLink}
                image={headerCard.image}
                subtitle={headerCard.subtitle}
                title={headerCard.title}
                body={headerCard.body}
                productName={productName}
              />
            </CtaCardContainer>
          ) : undefined}
        </StyledContentWrapper>
      ) : undefined}
      {showAnchorLinks && anchorLinks && (
        <StyledAnchorLinks>
          <AnchorLabel data-testid="anchor-link-label">
            {LABELS.navigate[lang]}
          </AnchorLabel>
          <StyledChainIcon color={bisColorsTheme.colors.abnNeutral400} />
          <ScrollLinkItems visible={anchorLinks.length > 0}>
            <HorizontalScrollWrapper
              childWidth={60}
              childGapWidth={24}
              scrollType={SCROLL_WRAPPER_TYPES.nav}
            >
              {anchorLinks.map(({ id, label }, key) => (
                <ScrollLink
                  data-testid="anchor-link"
                  key={key}
                  to={parseSectionID(id, label)}
                  spy
                  activeClass="active"
                  smooth
                  duration={600}
                  offset={0}
                  delay={50}
                  data-element-type="inpage navigation"
                  data-component-type="ProductNavigation"
                  data-element-label={label.toLowerCase()}
                >
                  {label}
                </ScrollLink>
              ))}
            </HorizontalScrollWrapper>
          </ScrollLinkItems>
        </StyledAnchorLinks>
      )}
    </Container>
  );
};

const MobileHeaderTile = ({
  title,
  subtitle,
  primaryLink,
  secondaryLink,
}: Props['header']) => (
  <MobileContentWrapper>
    <MobileTitlesWrapper>
      {title && <Title mobile>{title}</Title>}
      {subtitle && <StyledHeroSubtitle mobile>{subtitle}</StyledHeroSubtitle>}
      {(primaryLink || secondaryLink) &&
        renderButtons(
          primaryLink,
          secondaryLink,
          BUTTON_THEMES.highlight,
          BUTTON_THEMES.default
        )}
    </MobileTitlesWrapper>
  </MobileContentWrapper>
);

export default GenericHeader;

const Container = styled.div<{ removeBackground?: boolean }>`
  box-sizing: border-box;
  position: relative;

  ${({ removeBackground }) =>
    !removeBackground
      ? css`
          background: ${Colors.white};
          background-color: rgb(255, 255, 255);
          box-shadow: rgb(34 34 34 / 2%) 0px 0px 6px -4px,
            rgb(34 34 34 / 15%) 0px 4px 8px -4px;
        `
      : undefined}
`;

const ScrollLinkItems = styled.div<{ visible: boolean }>`
  display: flex;
  flex-direction: column;

  opacity: ${({ visible }) => (visible ? 1 : 0)};
  overflow: hidden;
  transition: opacity 0.3s ease-in;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledGatsbyImage = styled(GatsbyImage).withConfig({
  shouldForwardProp: (prop) => !['alignToContent'].includes(prop),
})<{ alignToContent: boolean }>`
  display: block;
  height: 200px;
  margin-bottom: ${({ alignToContent }) =>
    alignToContent ? `-${Spacings.XXXL}` : '0px'};
  position: relative;

  ${mq.from.breakpoint.M`
    height: 440px;
  `}

  &:after {
    background: linear-gradient(
      79.33deg,
      rgba(0, 0, 0, 0.5) 0.58%,
      rgba(0, 0, 0, 0.36) 18.66%,
      rgba(255, 255, 255, 5e-5) 57.93%
    );
    background-blend-mode: multiply, normal;
    bottom: 0%;
    content: '';
    display: block;
    height: 100%;
    left: 0%;
    position: absolute;
    right: 0%;
    top: 0%;
    width: 100%;
  }
`;

const ExtendedContentWrapper = styled(ContentWrapper)`
  ${AbsoluteTop}
`;

const MobileContentWrapper = styled(ContentWrapper)`
  background: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnPopover};
  color: ${Colors.abnBlack};
  display: block;
  margin: 0 16px;
  position: relative;
  top: -22px;

  ${mq.from.breakpoint.M`
    display: none;
  `}
`;

const StyledBreadcrumbsWrapper = styled.div`
  padding-top: ${Spacings.S};
  position: absolute;
  top: 0;
`;

const TitlesWrapper = styled.div`
  bottom: 40%;
  color: ${Colors.white};
  display: none;
  position: absolute;
  transform: translateY(50%);

  ${mq.from.breakpoint.M`
    bottom: 64px;
    display: block;
    transform: initial;
    max-width: 785px;
  `}

  ${mq.from.breakpoint.L`
    max-width: calc(100% - 408px - 120px);
  `}
`;

const MobileTitlesWrapper = styled.div`
  max-width: 785px;
  padding: 16px 0;
  width: 100%;
`;

const Title = styled(H1)<{ mobile?: boolean }>`
  margin-bottom: 8px;
  margin-top: 0;
`;

const AnchorLabel = styled.span`
  color: ${({ theme }) => theme.colors.abnNeutral400};
  font-size: 14px;
  white-space: nowrap;
`;

const StyledContentWrapper = styled(ContentWrapper)`
  display: flex;
  flex-direction: column;
  padding-bottom: 26px;

  ${mq.from.breakpoint.M`
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: 40px;
  `}
`;

const TextContainer = styled.div<{ extraCard?: boolean }>`
  margin-top: ${({ extraCard }) => (extraCard ? '48px' : '10px')};
  max-width: 785px;
  order: 2;
  padding-right: 0;
  width: 100%;

  ${mq.from.breakpoint.M`
    padding-right: 24px;
    max-width: 75%;
    margin: 40px auto 0;
    order: 1;
  `}

  ${mq.from.breakpoint.L`
    max-width: 840px;
  `}
`;

const CtaCardContainer = styled.div`
  margin-top: -1.5rem;
  min-width: auto;
  order: 1;
  width: 100%;

  ${mq.from.breakpoint.M`
    order: 2;
  `}

  ${mq.from.breakpoint.L`
    margin-top: -19.5rem;
    max-width: 408px;
    order: 2;
  `}

  ${mq.from.breakpoint.XL`
    margin-top: -15.5rem;
  `}
`;

const StyledChainIcon = styled(ChainIcon)`
  cursor: pointer;
  flex-shrink: 0;
  height: 18px;
  margin: 0 16px 0 8px;

  ${mq.from.breakpoint.M`
    height: 24px;
    margin: 0 24px 0 8px;
  `}
`;

const ScrollLink = styled(Link)`
  color: ${({ theme }) => theme.colors.abnGreen300};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: 14px;
  line-height: 20px;
  position: relative;
  white-space: nowrap;

  &:last-of-type {
    padding-right: 0;
  }

  &.active {
    color: ${Colors.abnButtonGreen};
  }

  &:after {
    background: ${Colors.abnButtonGreen};
    bottom: 0;
    content: '';
    height: 2px;
    left: 0;
    opacity: 0.75;
    position: absolute;
    width: 0%;
  }

  &:hover {
    color: ${Colors.abnButtonGreen};

    &:after {
      transition: width 0.2s ease;
      width: 100%;
    }
  }
`;

const StyledAnchorLinks = styled(ContentWrapper)`
  display: none;

  ${mq.from.breakpoint.M`
    align-items: center;
    display: flex;
    padding-bottom: 16px;
  `}
`;

const StyledHeroSubtitle = styled(HeroSubtitle)<{ mobile?: boolean }>`
  margin-bottom: 0;
`;

const StyledIntroText = styled(IntroText)`
  & > p:first-of-type {
    margin-top: 0;
  }
  & > p:last-of-type {
    margin-bottom: 0;
  }
`;

const StyledCtaWrapper = styled(CtaWrapper)`
  margin-top: 16px;

  ${mq.from.breakpoint.M`
    flex-direction: row;
    align-items: baseline;

    & > a {
      width: auto;
    }
  `}

  ${mq.from.breakpoint.L`
    margin-top: 32px;
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyles.primaryButtonPosition}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;
